import type {
    ColumnIndexWithScope,
    ExtendedGrid,
    FooterAggregates,
    MassOperationsCallbackProps,
    ShowCheckListEmptyObjectProp,
    ShowCheckListFieldKeysProp
} from '../types/modules/kendo-grid';

function clickedDataItem(e: HTMLElement, grid: kendo.ui.Grid | undefined) {
    const row = $(e).closest('tr');
    return grid?.dataItem(row);
}

function gridEditRowByItemId(grid: kendo.ui.Grid, itemId: number) {
    const dataItem = grid.dataSource.get(itemId);
    const row = grid.tbody.find(`tr[data-uid='${dataItem.uid}']`);
    grid.select(row);
    grid.editRow(row);
}

/**
 * Получаем индекс колонки и скоуп, в котором находятся ее значения
 * */
function getColumnIndexWithScopeByField(grid: kendo.ui.Grid, field: string): ColumnIndexWithScope {
    const column = grid.wrapper.find(`.k-grid-header th[data-field=${field}]`);

    return {
        columnIndex: column.index(),
        scope: column.closest('.k-grid-header-locked').length ? grid.lockedContent : grid.content
    };
}

/**
 * Использовать getColumnIndexWithScopeByField
 * TODO: в будущем удалить и заменить на getColumnIndexWithScopeByField
 * */
function getColumnIndexByField(grid: kendo.ui.Grid, field: string) {
    if (grid.wrapper.find(`.k-grid-content-locked`).length > 0) {
        const headers = grid.wrapper.find(`.k-grid-header`).children();
        return headers.first().find(`[data-field=${field}]`).length !== 0
               ? -headers.first().find(`[data-field=${field}]`).index() - 2
               : headers.last().find(`[data-field=${field}]`).index();
    }
    else {
        return grid.wrapper.find(`.k-grid-header [data-field=${field}]`).index();
    }
}

function getSelectedRowsData(grid: kendo.ui.Grid, returnType: 'ids' | 'objects') {
    const checkedCheckboxes = grid.element.find('[data-mass-operation] input:checked');
    const items = checkedCheckboxes.map((_, input) => {
        const masterRow = input.closest<HTMLTableRowElement>('tr');
        const uId = masterRow?.dataset?.uid ?? '';
        const item = grid.dataSource.getByUid(uId);
        return returnType === 'ids' ? item.get('Id') : item;
    });

    return items.get();
}

function selectedKeys(grid: kendo.ui.Grid) {
    return getSelectedRowsData(grid, 'ids');
}

function getSelectedItems(grid: kendo.ui.Grid) {
    return getSelectedRowsData(grid, 'objects');
}

function initEventCheckboxGridBody(grid: kendo.ui.Grid, callback?: (props: MassOperationsCallbackProps) => void) {
    const gridElement = grid.element.get(0);
    const isMasterRow = (row: HTMLTableRowElement) => !row.classList.contains('k-group-footer') && !row.classList.contains('k-grouping-row');

    grid.element.find('tbody').on('click', (e: JQuery.ClickEvent) => {
        const target = e.target as HTMLElement;
        /**
         * Игнорируем клики по кнопкам в колонках с действиями:
         * "Выгрузить", "Печать" и тд...
         * */
        if (target.closest('.k-command-cell button')) return;

        const clickedRow = target.closest('tr');
        const lockedContentBody = gridElement?.querySelector<HTMLElement>('.k-grid-content-locked tbody');
        const contentBody = gridElement?.querySelector<HTMLElement>('.k-grid-content tbody');
        const contentMasterRows = [...contentBody?.children ?? []].filter(isMasterRow) as HTMLTableRowElement[];
        const lockedContentMasterRows = [...lockedContentBody?.children ?? []].filter(isMasterRow) as HTMLTableRowElement[];

        if (clickedRow instanceof HTMLTableRowElement && isMasterRow(clickedRow)) {
            /**
             * Запрещает отрабатывать всем событиям ранее вызванным на tbody.
             * Учитывать, при дальнейшей разработке, что остальные клики на tbody не принесут результатов,
             * текущая функция initEventCheckboxGridBody прерывает все остальные события
             * */
            e.stopImmediatePropagation();

            const tbody = clickedRow.closest<HTMLElement>('tbody');
            const masterRows = [...tbody?.children ?? []].filter(isMasterRow) as HTMLTableRowElement[];
            const currentRowIndex = masterRows.findIndex(row => row === clickedRow);
            const checkboxControl = gridElement?.querySelectorAll<HTMLInputElement>('input.k-select-checkbox')[currentRowIndex + 1];

            lockedContentBody && toggleRowSelection(lockedContentMasterRows[currentRowIndex]);
            toggleRowSelection(contentMasterRows[currentRowIndex]);

            if (checkboxControl) {
                const isSelected = clickedRow.classList.contains('k-selected');
                checkboxControl.checked = isSelected;
                checkboxControl.setAttribute('aria-checked', String(isSelected));
            }

            callback?.({
                clickedRow,
                checkboxControl
            });
        }
    });
}

function toggleRowSelection(row?: HTMLTableRowElement) {
    row?.classList.toggle('k-state-selected');
    row?.classList.toggle('k-selected');
}

function initKendoGridSticky(grid: JQuery<HTMLElement>) {
    if (!grid.find('.k-grid-header-sticky').length) return;

    $('.k-grid-header').on('mousedown', function ({ target }) {
        if (target.classList.contains('k-resize-handle')) {
            function getLeft(el: HTMLElement) {
                return +el.style.left.replace(/[^0-9.]/gi, '');
            }

            const resizeLeft = getLeft(target);

            function removeLeft(arrElements: JQuery<HTMLElement>) {
                arrElements.each((_, el) => {
                    if (resizeLeft < getLeft(el)) {
                        el.style.removeProperty('left');
                    }
                });
            }

            const gridHeader = $(this);

            removeLeft(gridHeader.find('.k-grid-header-sticky'));
            removeLeft(gridHeader.next().find('.k-grid-content-sticky'));
        }
    });
}

function initKendoGrid() {
    $('.k-grid').each(function () {
        const element = $(this);
        const grid = element.data('kendoGrid') || element.data('kendoTreeList');

        const autoFitColumn = () => grid?.autoFitColumn(0);
        const autoFitColumnTimeout = () => setTimeout(autoFitColumn);
        const kendoResize = () => setTimeout(() => kendo.resize(element, false));
        const kendoResizeTimeout = () => setTimeout(kendoResize);

        const isTreeList = element.hasClass('k-treelist');
        if (isTreeList) {
            if (element.hasClass('unautoresizable-treelist')) return;

            grid?.bind('expand', autoFitColumnTimeout);
            grid?.bind('collapse', autoFitColumnTimeout);
            grid?.bind('dataBound', autoFitColumnTimeout);
            $('#expandCollapseButtons_btExpandFirstLevel, #expandCollapseButtons_btCollapseFirstLevel').on('click', autoFitColumnTimeout);
            autoFitColumnTimeout();
        }
        else {
            kendoResizeTimeout();
        }

        $(window).on('resize', () => {
            if (isTreeList) {
                autoFitColumn();
            }
            else {
                kendoResize();
            }
        });

        initKendoGridSticky(element);
    });
}

function selectedLinks(grid: kendo.ui.Grid, field: string) {
    return $('.k-grid-content-sticky').find('input[aria-checked=\'true\']').map(function () {
        return grid.dataSource.getByUid($(this).parent().parent().data('uid')).get(field);
    }).get();
}

function headerCheckbox_bindCheckEvent(callback?: (isChecked: boolean) => void) {
    const checkboxGrid = $('.k-grid').has('.k-checkbox');
    const headChb = checkboxGrid.find('thead .k-checkbox');

    headChb.get(0)!.onchange = () => {
        const isChecked = headChb.is(':checked');
        const tableRows = checkboxGrid.find('tbody').find('tr');
        const rowCheckbox = tableRows.find('input[type="checkbox"].k-select-checkbox');

        if (isChecked) {
            tableRows.addClass('k-state-selected k-selected');
            rowCheckbox.prop('checked', true).attr('aria-checked', 'true');
        }
        else {
            tableRows.removeClass('k-state-selected k-selected');
            rowCheckbox.prop('checked', false).attr('aria-checked', 'false');
        }

        callback?.(isChecked);
    };
}

function toggleDisablePopupButton(isDisabled: boolean) {
    this.element.find('.k-grid-update').attr('disabled', isDisabled);
}

function showCheckList(urlAction: string, fieldKeys: ShowCheckListFieldKeysProp, emptyObject: ShowCheckListEmptyObjectProp) {
    return new Promise((resolve, reject) => {
        const wrapperId = $('#checkList');
        wrapperId.empty();
        window.ajaxGet(urlAction, null, (result): undefined => {
            $('#tableCheckList').show();
            emptyObject.name = result.length > 0 ? result[0][fieldKeys.id] : 0;
            for (let i = 0; i < result.length; i++) {
                wrapperId.append(`
                    <tr class="check_list_data" id="${result[i][fieldKeys.unitId]}" weight="${result[i][fieldKeys.weight]}">
                        <td class="k-master-row">
                            ${result[i][fieldKeys.name]}
                        </td>
                        <td class="k-master-row px-2">
                            <div class="k-input k-rounded-md custom-select">
                                <select class="k-input-inner check_list_element cursor-pointer" id="${result[i][fieldKeys.unitId]}">
                                    <option selected class="k-list-item"></option>
                                    <option value="true" class="k-list-item">Да</option>
                                    <option value="false" class="k-list-item">Нет</option>
                                </select>
                            </div>
                        </td>
                    </tr>
                `);
            }
            resolve(result);
        }, (e) => {
            window.standartErrorAnswer();
            reject();
            return e;
        }, (e) => {
            window.lockPage(false);
            reject();
            return e;
        });
    });
}

function gridRowDblClickEdit(e: JQuery.DoubleClickEvent) {
    const selectedRow = $<HTMLElement>(e.currentTarget);
    const data = selectedRow.closest('.k-grid').data();
    const [_, key] = Object.keys(data);
    const instance: kendo.ui.Grid | kendo.ui.TreeList = data[key];

    instance.editRow(selectedRow);
}

function gridRowRightClick(e: JQuery.MouseDownEvent) {
    if (e.which === 3) {
        const currentElement = $(this);
        const selectedClass = 'k-selected';
        const grid = currentElement.closest('.k-grid').data('kendoGrid');
        const uid = currentElement.attr('data-uid');
        const lockedRow = grid?.lockedTable?.find(`tr[data-uid="${uid}"]`);

        $(`tr.${selectedClass}`).removeClass(selectedClass);
        currentElement.addClass(selectedClass);
        lockedRow?.addClass(selectedClass);
    }
}

function setDefaultSaveButtonText() {
    const saveWndBtns = document.querySelectorAll('.k-window-actions [ref-update-button], .k-window-actions [data-command="update"]');
    saveWndBtns.forEach((btn) => {
        btn.classList.add('k-grid-update');
        btn.innerHTML = `✔ Сохранить`;
    });

    const cancelWndBtns = document.querySelectorAll('.k-window-actions [ref-cancel-button], .k-window-actions [data-command="cancel"]');
    cancelWndBtns.forEach((btn) => {
        btn.classList.add('k-grid-cancel');
    });
}

function setPopupTitle(e: kendo.ui.GridEditEvent, title: string) {
    e.container?.data('kendoWindow')?.title(title);
}

async function grid_onError(e: kendo.data.DataSourceErrorEvent) {
    window.validationMessageTmpl = kendo.template($('#validationMessage').html());

    if (e.hasOwnProperty('errors') && e.errors) {
        let grid: kendo.ui.Grid | undefined;
        let treeList: kendo.ui.TreeList | undefined;

        $('.k-grid').each(function () {
            const tmp = $(this).data('kendoGrid');
            if (tmp?.dataSource === e.sender) {
                grid = tmp;
            }
        });

        grid?.one('dataBinding', (ev: kendo.ui.GridDataBindingEvent) => {
            // cancel grid rebind if error occurs
            ev.preventDefault();
        });

        treeList?.one('dataBinding', (ev: kendo.ui.TreeListDataBindingEvent) => {
            // cancel treeList rebind if error occurs
            ev.preventDefault();
        });

        for (const error in e.errors) {
            if (e.errors.hasOwnProperty(error)) {
                void window.swal.fire('Ошибка!', e.errors[error].errors.toString(), 'error');
            }
        }
    }
    else if (e.hasOwnProperty('status') && e.status === 'error') {
        await window.swal.fire('Ошибка!', e.xhr.responseJSON.Message, 'warning');
    }

    /**
     * Workaround for Kendo UI Grid popup editor: after server-side validation,
     * the Save button keeps the `k-disabled` class and does not become active again.
     * Kendo closed https://github.com/telerik/kendo-ui-core/issues/8402 without a planned fix,
     * so the class is removed manually in our code.
     */
    document.querySelector('.k-window-actions .k-grid-update')?.classList.remove('k-disabled');
}

function grid_onColumnHiddenChange({ sender }: kendo.ui.GridColumnHideEvent) {
    sender.element.addClass('fix-width-table');
    adjustGridHeight();
}

// Customize the hint
function hintElement(element: JQuery<HTMLElement>) {
    const grid = element.closest('.k-grid.k-widget').data('kendoGrid');
    const table = grid?.table.clone();
    const wrapperWidth = grid?.wrapper.width() ?? 0;
    const wrapper = $('<div class=\'k-grid k-widget\'></div>').width(wrapperWidth);

    // Remove Grid's header from the hint
    table?.find('thead').remove();
    // Remove the existing rows from the hint
    table?.find('tbody').empty();
    // Remove the existing rows from the hint
    table?.find('tfoot').empty();
    // Wrap the table
    table?.wrap(wrapper);
    // Append the dragged element
    table?.append(element.clone().removeAttr('uid'));
    table?.find('tr').css('background-color', '#a3d0e4');

    // Return the hint element
    return table?.parent();
}

function getVisibleColumns(grid: kendo.ui.Grid, withSettings: boolean = true) {
    const settings = window.getSettings();
    if (settings && withSettings) return settings.columns;
    return grid.columns.filter(c => !c.hidden).map(c => c.field).filter(c => c);
}

function rebindOnChangeColumns(e: kendo.ui.GridColumnMenuInitEvent) {
    compareAndRebindColumns(e, true);
}

function rebindOnChangeColumnsWithoutSettings(e: kendo.ui.GridColumnMenuInitEvent) {
    compareAndRebindColumns(e, false);
}

function compareAndRebindColumns({ sender, container }: kendo.ui.GridColumnMenuInitEvent, withSettings: boolean) {
    const visibleColumns = getVisibleColumns(sender, withSettings);

    container?.data('kendoPopup')?.bind('close', () => {
        const actualState = getVisibleColumns(sender, withSettings);
        const isEquals = actualState.length === visibleColumns.length && visibleColumns.every((v, i) => v === actualState[i]);
        if (!isEquals) sender.dataSource.read();
    });
}

function grid_onEdit() {
    setDefaultSaveButtonText();
    window.setControlsSettings();
}

function adjustGridHeight() {
    $('.k-grid:not(.no-adjust)').each(function () {
        let height = window.substractCommonHeight(window.innerHeight + 17);
        const grid = $(this).data('kendoGrid');
        const tree = $(this).data('kendoTreeList');

        if (grid?.content) {
            grid.content.css('height', `${height}px`);
        }
        if (grid?.lockedContent) {
            grid.lockedContent.css('height', `${height - (grid.content[0].scrollWidth > grid.content[0].clientWidth ? 17 : 0)}px`);
        }

        if (tree?.content) {
            tree.content.css('height', `${height}px`);
        }

        if (tree?.lockedContent) {
            tree.lockedContent.css('height', `${height - (tree.content[0].scrollWidth > tree.content[0].clientWidth ? 17 : 0)}px`);
        }
    });
}

function setGridHeightByContent() {
    const height = window.substractCommonHeight(window.innerHeight - 5);
    const heightContentTable = $('.k-grid-content table').innerHeight();
    if (!heightContentTable) return;
    $('.k-grid-content').css('height', `${heightContentTable > height ? height : heightContentTable}px`);
}

function getState(grid: kendo.ui.Grid | kendo.ui.TreeList) {
    const dataSource = grid.dataSource;
    const columns = grid.columns
        .filter((column: kendo.ui.GridColumn | kendo.ui.TreeListColumn) => !column.hidden)
        .map((c: kendo.ui.GridColumn | kendo.ui.TreeListColumn) => c.field);

    return kendo.stringify({
        sort: dataSource.sort(),
        page: dataSource.page(),
        pageSize: dataSource.pageSize(),
        group: dataSource.group().map((c: { field: string; dir: string; }) => ({
            field: c.field,
            dir: c.dir
        })),
        columns: columns
    });
}

function getGridId(grid: kendo.ui.Grid | kendo.ui.TreeList) {
    return grid?.wrapper ? grid.wrapper.attr('id') : null;
}

function findFirstGrid() {
    const gridElements = $('.k-grid:not(.grid-without-settings)');

    if (!gridElements.length) {
        console.warn('Not found any grids!');
        return null;
    }

    let firstGrid: kendo.ui.Grid | kendo.ui.TreeList | undefined = gridElements.first().data('kendoGrid');

    if (!firstGrid) {
        firstGrid = gridElements.first().data('kendoTreeList');
    }

    if (firstGrid && gridElements.length > 1) {
        console.warn(`Found ${gridElements.length} grid elements! Using first: ${getGridId(firstGrid)}`);
    }

    return firstGrid;
}

function filterAggregates(aggregates: CustomAggregateItem[], groups: GridGroupColumn[]) {
    if (!aggregates) return [];

    return aggregates.filter(({ aggregate, field, footerTemplate }) =>
        (aggregate !== 'count' || footerTemplate?.includes('count')) || groups.some(group => group.field === field)
    );
}

/**
 * Поле fields единственное, которое хранит агрегации не тронутыми, но хранит их в таком виде:
 * aggregates: string[], нам же нужно разложить этот массив, в итоге
 * из массива колонок грида нужно получить плоский массив таких элементов:
 * {
 *     field: string;
 *     aggregate: 'count' | 'sum' | 'min' | 'max' | 'average'
 *     footerTemplate?: string
 * }
 * */
function getAggregatesFromFields(fields: GridColumn[]) {
    return fields.map((item) => ({ field: item.field, aggregate: item.aggregates, footerTemplate: item.footerTemplate }))
        /**
         * Фильтруем по наличию агрегации в колонке, т.к. есть колонки без агрегаций
         * */
        .filter(({ aggregate }) => aggregate)
        .flatMap(({ field, aggregate, footerTemplate }) =>
            aggregate.map((type) => ({ field, aggregate: type, footerTemplate }))
        );
}

/**
 * Исправление бага с появлением белого прямоугольника на macOS устройствах
 * в области горизонтального scrollbar у Locked контента
 * */
function fixHeightForGridWithLockedContent(instanceGrid?: ExtendedGrid) {
    if (instanceGrid?.lockedContent) {
        instanceGrid.bind('dataBound', ({ sender }: kendo.ui.GridDataBoundEvent) => {
            window.isMacOS() && (
                sender.lockedContent.height(sender.content?.height() ?? 0)
            );

            isLimitedHeight(instanceGrid.element) && (
                window.fixLimitedHeightLockedContent(instanceGrid)
            );
        });

        instanceGrid.bind('columnResize', (e: kendo.ui.GridEvent) => {
            isLimitedHeight(instanceGrid.element) && (
                fixLockedContentHeightAtColumnResize(e)
            );
        });

        instanceGrid.bind('columnShow', (e: kendo.ui.GridEvent) => {
            fixGridContentWidth(e);
        });

        instanceGrid.bind('columnHide', (e: kendo.ui.GridEvent) => {
            fixGridContentWidth(e);
        });
    }
}

function isLimitedHeight(element: JQuery<HTMLElement>) {
    return Boolean(element.closest('.grid-auto-height.grid-limited-height-500')?.length);
}

function setSettingsGrid(grid: kendo.ui.Grid | kendo.ui.TreeList) {
    const settings = window.getSettings();

    if (settings) {
        const { page, pageSize, columns, group } = settings;
        const gridColumns: GridColumn[] = grid.columns;
        const dataSource = grid.dataSource as PatchedGridDataSource;
        const defaultGroups: GridGroupColumn[] = dataSource.group();
        const newGroup: GridGroupColumn[] = new Array();
        const isServerAggregates = dataSource.options.serverAggregates;

        const filteredAggregates = isServerAggregates
            ? filterAggregates(getAggregatesFromFields(gridColumns), group)
            : dataSource.aggregate();

        group.forEach((group) => {
            const foundColumn = gridColumns.find(({ field }) => field === group.field);
            const isDefault = defaultGroups.find(({ field }) => field === group.field);

            if (foundColumn && isDefault) {
                newGroup.push(isDefault);
            }
            else if (foundColumn && !isDefault) {
                group.aggregates = filteredAggregates;
                newGroup.push(group);
            }
        });

        dataSource._aggregate = filteredAggregates;
        dataSource._group = newGroup;
        dataSource._pageSize = pageSize;

        const [hasSavedSort] = settings.sort ?? [];
        const [sortGrid] = dataSource.sort() ?? [];

        if (hasSavedSort && sortGrid && (sortGrid.dir !== hasSavedSort.dir || sortGrid.field !== hasSavedSort.field)) {
            for (const { field, sortable } of gridColumns) {
                if (hasSavedSort.field === field) {
                    if (sortable === false)
                        break;

                    dataSource._sort = [hasSavedSort];
                    break;
                }
            }
        }

        gridColumns.forEach(({ field }) => {
            const foundColumn = columns.find((column) => column === field);
            grid[!foundColumn && field ? 'hideColumn' : 'showColumn'](field);
        });

        grid.one('dataBound', () => {
            dataSource._pageSize = pageSize;

            if (page > 1 && grid instanceof kendo.ui.Grid) {
                grid.pager.page(page);
            }
        });
    }

    $('[data-role=grid]').each((_, element) => {
        const instance = $(element).data('kendoGrid') as ExtendedGrid | undefined;

        fixHeightForGridWithLockedContent(instance);

        if (!instance?.dataSource.options.serverAggregates)
            return;

        /**
         * Подписываемся на событие group, чтобы в ручном режиме фильтровать агрегации
         * после запускаем группировку с нужными агрегациями
         * */
        instance?.bind('group', (event: GridGroupEvent) => onGroupGridHandle(event, element));

        if (!settings) {
            /**
             * если на странице нет информации в URL - подписываемся на событие requestStart, отменяем его,
             * фильтруем агрегации и в ручном режиме запускаем запрос
             * */
            instance?.dataSource.one('requestStart', (event: DataSourceRequestStartEvent) => {
                event.preventDefault();

                const group = event.sender.options.group || [] as GridGroupColumn[];
                const aggregates = getAggregatesFromFields(event.sender.options.fields);
                event.sender._aggregate = filterAggregates(aggregates || [], group);
                instance?.dataSource.read();
            });
        }
    });
}

function onGroupGridHandle(event: GridGroupEvent, nativeElement: HTMLElement) {
    event.preventDefault();

    const { groups, sender } = event;
    /**
     * Проверяем, нужна ли группировка после нажатия на какую то кнопку, или нет
     * Потому что есть страницы, где Антон просил делать такую реализацию
     * */
    const isManualGrouping = nativeElement.hasAttribute('data-manual-grouping');
    const dataSource = sender.dataSource as PatchedGridDataSource;
    /**
     * Берём агрегации из options.dataSource.fields(это массив колонок),
     * т.к. здесь они хранятся в неизмененном виде, но нужно привести это к нашему рабочему виду:
     * */
    const aggregatesFromFields = getAggregatesFromFields(sender.options.dataSource.fields as GridColumn[]);
    const filteredAggregates = filterAggregates(aggregatesFromFields, groups);
    const updatedGroups = groups.map((group) => ({ ...group, aggregates: filteredAggregates }));

    dataSource._aggregate = filteredAggregates;
    isManualGrouping ? dataSource._group = updatedGroups : dataSource.group(updatedGroups);
}

function configureResizableGrid(instance: kendo.ui.Grid) {
    const gridParent = instance.wrapper.parent();
    const paddingBottom = 10;

    applyGridParentStyles(gridParent, gridParent.offset()!.top, paddingBottom);

    $(window).on('resize', () => {
        if (instance && instance.resize) {
            instance.resize();
            applyGridParentStyles(gridParent, gridParent.offset()!.top, paddingBottom);
        }
    });
}

function applyGridParentStyles(grid: JQuery<HTMLElement>, heightValue: number, paddingBottom: number) {
    grid.css({
        'height': `calc(100vh - ${heightValue}px)`,
        'padding-bottom': `${paddingBottom}px`
    });
}

function changeFooterTemplateForGrid(response: Record<string, number>, grid: ExtendedGrid, fields: string[]) {
    if (grid.footerTemplate) {
        const aggregates = grid.dataSource.aggregates();

        fields.forEach(field => {
            if (aggregates[field]) {
                if (aggregates[field].hasOwnProperty('sum')) {
                    aggregates[field] = { sum: response[field] ?? '' };
                }
                else if (aggregates[field].hasOwnProperty('average')) {
                    aggregates[field] = { average: response[field] ?? '' };
                }
                else if (aggregates[field].hasOwnProperty('count')) {
                    aggregates[field] = { count: response[field] ?? '' };
                }
            }
        });

        // Для обновления агрегаций в футере грида у которого есть колонки с locked
        if ($('.k-grid-content-locked').length) {
            window.updateLockedFooterTemplate(grid, aggregates);
            return;
        }

        grid.footer.find('.k-footer-template').replaceWith(grid.footerTemplate(aggregates));
    }
}

function updateLockedFooterTemplate(grid: ExtendedGrid, aggregates: FooterAggregates = {}) {
    setTimeout(() => {
        const footerElems = $(grid.footerTemplate(aggregates));
        const lockedCellsCount = grid.element.find('.k-grid-header-locked th')
        const footerLockedElems = footerElems.find('td').slice(0, lockedCellsCount.length);
        footerLockedElems.remove();
        grid.footer.find('.k-grid-footer-wrap .k-footer-template').replaceWith(footerElems);
        grid.footer.find('.k-grid-footer-locked .k-footer-template').replaceWith(footerLockedElems);
    });
}

function fixLockedContentHeightAtColumnResize(e: kendo.ui.GridEvent) {
    fixLimitedHeightLockedContent(e.sender as ExtendedGrid);
}

/**
 * Фикс высоты k-grid-content-locked если у грида есть классы
 * grid-auto-height и grid-limited-height-500
 * */
function fixLimitedHeightLockedContent(grid: ExtendedGrid) {
    const lockedContent = grid.element.find('.k-grid-content-locked').get(0);
    if (lockedContent) {
        lockedContent.style.maxHeight = hasHorizontalScrollbar(grid) ? 'calc(500px - 17px)' : '500px';
    }
}

function hasHorizontalScrollbar(grid: ExtendedGrid) {
    const contentElement = grid.element.find('.k-grid-content').get(0);
    return contentElement && (contentElement.scrollWidth > contentElement.clientWidth);
}

/**
 * Фикс колонок в футере, после оптимизации агрегаций в гриде
 * */
function syncFooterColumnsWidth(grid: ExtendedGrid) {
    const contentTable = grid.table;
    const footerTable = grid.footer?.find('.k-grid-footer-wrap table');

    if (!contentTable || !footerTable) return;

    const contentCols = contentTable.find('col');
    const footerCols = footerTable.find('col');

    if (contentCols.length !== footerCols.length) return;

    contentCols.each((index, col) => {
        const contentWidth = col.offsetWidth;
        footerCols.eq(index).css('width', `${contentWidth}px`);
    });
}

window.monthDays = [
    'First',
    'Second',
    'Third',
    'Forth',
    'Fifth',
    'Sixth',
    'Seventh',
    'Eighth',
    'Ninth',
    'Tenth',
    'Eleventh',
    'Twelfth',
    'Thirteenth',
    'Fourteenth',
    'Fifteenth',
    'Sixteenth',
    'Seventeenth',
    'Eighteenth',
    'Nineteenth',
    'Twenty',
    'TwentyFirst',
    'TwentySecond',
    'TwentyThird',
    'TwentyForth',
    'TwentyFifth',
    'TwentySixth',
    'TwentySeventh',
    'TwentyEighth',
    'TwentyNinth',
    'Thirty',
    'ThirtyFirst'
];

function refreshCalendar(groupColumns: GridColumn[], grid: kendo.ui.Grid) {
    const newOptions = Object.assign({}, grid.getOptions());
    newOptions.columns = newOptions.columns?.filter(col => col.field && !window.monthDays.includes(col.field) && !col.field.startsWith('day_'));
    newOptions.columns = newOptions.columns?.concat(groupColumns);
    grid.setOptions(newOptions);
}

/**
 * Получение колонки из календаря при двойном клике, для смены статуса
 * */
function getCalendarColumnByCell(td: HTMLTableCellElement, grid: kendo.ui.Grid) {
    const cellIndex = $(td).parent().children().index(td);
    const allColumns = grid.options.columns;
    if (!allColumns) return;
    const countLocked = allColumns?.filter(col => col.locked).length;
    return (cellIndex < 0 || cellIndex >= allColumns.length) ? null : allColumns[cellIndex + countLocked];
}

/**
 * Подставляет в грид автосуммы, переданные с сервера
 * */
function backDataSource_onRequestEnd(e: kendo.data.DataSourceRequestEndEvent, grid: ExtendedGrid) {
    if (e.type !== 'read' || !e.response.Total) return;

    const useKendoAutoSums = grid.element.data('useKendoAutoSums');
    if (useKendoAutoSums && !e.response.AggregateResults) return;

    const aggregates: Record<string, number> = {};

    // прокидываем автосуммы из ответа сервера
    const responseAggregates = e.response.AggregateResults || [];
    responseAggregates.forEach((aggregate: AggregateResult) => {
        aggregates[aggregate.Member] = aggregate.Value;
    });

    // обнуляем все остальные автосуммы
    const gridAggregates = e.sender?.aggregate()?.map((i: kendo.data.DataSourceAggregateItem) => i.field) || [];
    gridAggregates.forEach((field: string) => {
        if (aggregates.hasOwnProperty(field)) return;
        aggregates[field] = 0;
    });

    const fields = Object.keys(aggregates);
    if (grid) {
        setTimeout(() => changeFooterTemplateForGrid(aggregates, grid, fields));
    }
}

/**
 * Синхронизирует высоту locked строк на основании обычных.
 * Используется, когда высота грида ломается из-за вставки в ячейки комбобоксов, календарей и т.д.
 * */
function syncRowHeights(grid: ExtendedGrid) {
    const contentRows = grid.tbody.find('.k-master-row');
    const lockedRows = grid.lockedTable.find('.k-master-row');

    contentRows.each((index, row) => {
        const contentRowHeight = $(row).height();
        const lockedRow = lockedRows.eq(index);
        contentRowHeight && lockedRow.height(contentRowHeight);
    });

    const gridContentHeight = grid.tbody.height();
    gridContentHeight && grid.lockedTable.height(gridContentHeight);
}

function onColumnMenuCloseUpdateFooter({ sender, container }: kendo.ui.GridColumnMenuInitEvent) {
    container?.data('kendoPopup')?.bind('close', () => {
        updateLockedFooterTemplate(sender as ExtendedGrid, sender.dataSource.aggregates());
    });
}

function fixGridContentWidth({ sender }: kendo.ui.GridColumnMenuInitEvent) {
    sender.refresh();
    setTimeout(() => {
        sender.refresh();
    }, 100);
}

window.findFirstGrid = findFirstGrid;
window.showCheckList = showCheckList;
window.getState = getState;
window.adjustGridHeight = adjustGridHeight;
window.applyGridParentStyles = applyGridParentStyles;
window.backDataSource_onRequestEnd = backDataSource_onRequestEnd;
window.changeFooterTemplateForGrid = changeFooterTemplateForGrid;
window.compareAndRebindColumns = compareAndRebindColumns;
window.clickedDataItem = clickedDataItem;
window.configureResizableGrid = configureResizableGrid;
window.filterAggregates = filterAggregates;
window.getGridId = getGridId;
window.getCalendarColumnByCell = getCalendarColumnByCell;
window.getColumnIndexByField = getColumnIndexByField;
window.getColumnIndexWithScopeByField = getColumnIndexWithScopeByField;
window.getSelectedItems = getSelectedItems;
window.getVisibleColumns = getVisibleColumns;
window.grid_onEdit = grid_onEdit;
window.grid_onColumnHiddenChange = grid_onColumnHiddenChange;
window.grid_onError = grid_onError;
window.gridEditRowByItemId = gridEditRowByItemId;
window.gridRowDblClickEdit = gridRowDblClickEdit;
window.gridRowRightClick = gridRowRightClick;
window.setSettingsGrid = setSettingsGrid;
window.hintElement = hintElement;
window.headerCheckbox_bindCheckEvent = headerCheckbox_bindCheckEvent;
window.initKendoGrid = initKendoGrid;
window.initEventCheckboxGridBody = initEventCheckboxGridBody;
window.initKendoGridSticky = initKendoGridSticky;
window.onGroupGridHandle = onGroupGridHandle;
window.rebindOnChangeColumns = rebindOnChangeColumns;
window.rebindOnChangeColumnsWithoutSettings = rebindOnChangeColumnsWithoutSettings;
window.refreshCalendar = refreshCalendar;
window.selectedKeys = selectedKeys;
window.selectedLinks = selectedLinks;
window.setDefaultSaveButtonText = setDefaultSaveButtonText;
window.setPopupTitle = setPopupTitle;
window.setGridHeightByContent = setGridHeightByContent;
window.toggleDisablePopupButton = toggleDisablePopupButton;
window.toggleRowSelection = toggleRowSelection;
window.updateLockedFooterTemplate = updateLockedFooterTemplate;
window.syncRowHeights = syncRowHeights;
window.fixLimitedHeightLockedContent = fixLimitedHeightLockedContent;
window.syncFooterColumnsWidth = syncFooterColumnsWidth;
window.onColumnMenuCloseUpdateFooter = onColumnMenuCloseUpdateFooter;